import api from './api';
import { API_BASE_URL } from '../lib/constants';
import { getTranslation } from '../i18n/translations/index.js';

const getLang = () => {
  try {
    return localStorage.getItem('language') || 'en';
  } catch (e) {
    return 'en';
  }
};

const resolveUrl = (u, base) => {
  if (!u) return null;
  if (u.startsWith('http://') || u.startsWith('https://')) return u;
  if (u.startsWith('/')) return `${base}${u}`;
  return `${base}/${u}`;
};

const pickFirstMessage = (values = []) => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return '';
};

export const extractBackendErrorMessage = (input) => {
  if (!input) return '';
  if (typeof input === 'string') {
    const msg = input.trim();
    if (!msg) return '';
    try {
      const parsed = JSON.parse(msg);
      const parsedMessage = extractBackendErrorMessage(parsed);
      return parsedMessage || msg;
    } catch (e) {
      return msg;
    }
  }
  if (Array.isArray(input)) {
    for (const item of input) {
      const message = extractBackendErrorMessage(item);
      if (message) return message;
    }
    return '';
  }
  if (typeof input === 'object') {
    const directMessage = pickFirstMessage([
      input.errorMessage,
      input.error,
      input.message,
      input.msg,
      input.detail,
      input.reason,
      input.description
    ]);
    if (directMessage) return directMessage;
    const nestedMessage = pickFirstMessage([
      extractBackendErrorMessage(input.data),
      extractBackendErrorMessage(input.errors),
      extractBackendErrorMessage(input.result)
    ]);
    return nestedMessage;
  }
  return '';
};

export async function generateVideo({
  type,
  prompt,
  model,
  videoMode,
  aspectRatio,
  videoResolution,
  videoDuration,
  waterMark,
  seed,
  startImage,
  endImage,
  keyImage
}) {
  const fd = new FormData();
  fd.append('type', type);
  fd.append('prompt', prompt || '');
  const requestMode = videoMode || 'FAST';
  fd.append('model', requestMode);
  fd.append('videoModel', model || '');
  fd.append('aspectRatio', aspectRatio || '16:9');
  if (videoResolution) fd.append('videoResolution', videoResolution);
  if (videoDuration) fd.append('videoDuration', videoDuration);
  if (waterMark) fd.append('waterMark', waterMark);
  if (typeof seed !== 'undefined' && seed !== null && seed !== '') fd.append('seed', parseInt(seed, 10));
  if (startImage) fd.append('startImage', startImage);
  if (endImage) fd.append('endImage', endImage);
  if (keyImage) fd.append('keyImage', keyImage);

  try {
    const response = await api.post('/video/generate', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      responseType: 'blob'
    });
    const ct = response.headers['content-type'] || '';
    if (ct.includes('application/json')) {
      const text = await response.data.text();
      const data = JSON.parse(text || '{}');
      const success = data?.success !== false;
      const payload = data?.data || {};
      if (!success || !payload?.id) {
        const err = new Error(extractBackendErrorMessage(data) || getTranslation('errorHandler.videoGenerateRequestFailed', getLang()));
        err.code = 'REQUEST_FAILED';
        err.payload = data;
        throw err;
      }
      return { success, id: payload?.id, status: payload?.status, data: payload };
    }
    const blob = response.data;
    const url = URL.createObjectURL(blob);
    return { url, blob };
  } catch (error) {
    if (error.response && error.response.data instanceof Blob) {
      try {
        const text = await error.response.data.text();
        const data = JSON.parse(text);
        error.response.data = data;
      } catch (e) {
      }
    }
    const backendMessage = extractBackendErrorMessage(error?.response?.data) || extractBackendErrorMessage(error);
    if (backendMessage) {
      error.message = backendMessage;
    }
    if (!error.code) {
      error.code = 'REQUEST_FAILED';
    }
    throw error;
  }
}

export async function pollVideoStatus(id, { intervalMs = 2000, timeoutMs = 300000 } = {}) {
  const start = Date.now();
  while (true) {
    try {
      const resp = await api.get(`/video/check/${id}`);
      const body = resp.data || {};
      const success = body?.success !== false;
      const payload = body?.data || {};
      const statusRaw = payload?.status || '';
      const status = String(statusRaw).toUpperCase();
      if (!success) {
        const err = new Error(extractBackendErrorMessage(body) || getTranslation('errorHandler.requestFailed', getLang()));
        err.code = 'REQUEST_FAILED';
        err.payload = body;
        throw err;
      }
      if (status === 'FAILED') {
        const err = new Error(extractBackendErrorMessage(payload) || getTranslation('errorHandler.generateFailed', getLang()));
        err.code = 'FAILED';
        err.payload = payload;
        throw err;
      }
      if (status === 'COMPLETED') {
        const videoUrl = resolveUrl(payload?.videoUrl, API_BASE_URL);
        const coverUrl = resolveUrl(payload?.coverImageUrl || payload?.imageUrl, API_BASE_URL);
        return { payload, videoUrl, coverUrl };
      }
      if (Date.now() - start > timeoutMs) {
        const err = new Error(getTranslation('errorHandler.generateTimeoutInfo', getLang()));
        err.code = 'TIMEOUT';
        err.payload = payload;
        throw err;
      }
      await new Promise((r) => setTimeout(r, intervalMs));
    } catch (error) {
      if (error?.code === 'FAILED' || error?.code === 'TIMEOUT' || error?.code === 'REQUEST_FAILED') {
        throw error;
      }
      const err = new Error(
        extractBackendErrorMessage(error?.response?.data) ||
        extractBackendErrorMessage(error) ||
        getTranslation('errorHandler.queryGenerateStatusFailed', getLang())
      );
      err.code = 'REQUEST_FAILED';
      err.payload = error?.response?.data;
      throw err;
    }
  }
}
